import { z } from "zod";

/**
 * A garage package (ADR 0007) is one garage's data as a portable zip. Its manifest says which
 * format it is in and which app wrote it, so an app can tell whether it can import it.
 */

/** The format this app writes. Bump it only for changes older packages can't follow (ADR 0007). */
export const GARAGE_PACKAGE_FORMAT = 3;
/** The oldest format this app still imports (by upgrading it step by step). */
export const GARAGE_PACKAGE_MIN_FORMAT = 1;

export const garagePackageManifest = z.object({
  app: z.literal("bimmertools-inventory"),
  kind: z.literal("garage-package"),
  format: z.int(),
  /** The app version that wrote the package. */
  appVersion: z.string().min(1),
  /** The writing app's newest database migration. */
  schema: z.string().min(1),
  /**
   * The language the package's data is in. Any code, so a package from an installation with more
   * languages still imports; a garage language this one doesn't ship is read as none.
   */
  language: z.string().min(1),
  exportedAt: z.string().min(1),
  /** Rows per table. */
  counts: z.record(z.string(), z.int().nonnegative()),
});

export type GaragePackageManifest = z.infer<typeof garagePackageManifest>;

export type PackageCompatibility = "ok" | "upgrade" | "tooNew" | "tooOld";

/**
 * Whether this app can import a package: "upgrade" means an older format that the importer
 * rewrites first. A package from an app with database migrations this one doesn't know is too
 * new, even in the same format, since its rows may carry columns this app can't store.
 */
export function packageCompatibility(
  manifest: Pick<GaragePackageManifest, "format" | "schema">,
  app: { format: number; minFormat: number; schemas: readonly string[] },
): PackageCompatibility {
  if (manifest.format > app.format) return "tooNew";
  if (manifest.format < app.minFormat) return "tooOld";
  if (!app.schemas.includes(manifest.schema)) return "tooNew";
  return manifest.format < app.format ? "upgrade" : "ok";
}
